import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Wallet, AlertCircle, History, Loader2, ArrowDownToLine, X, CheckCircle2, Clock, Info, Trophy, RefreshCw } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import { useWallet } from "@/context/WalletContext";
import { toast } from "sonner";
import { getWallet, getWithdrawHistory, getSettings, requestWithdraw, compound } from "@/lib/api";
import { notifySuccess } from "@/lib/notify";
import IncomeHistory from "@/components/IncomeHistory";

const usd2 = (n) => Number(n || 0).toFixed(2);

function Gauge({ pct = 0 }) {
  const r = 60, cx = 80, cy = 80, circ = Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div className="relative mx-auto h-[100px] w-[160px]">
      <svg viewBox="0 0 160 90" className="h-full w-full">
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="rgba(60,107,51,0.35)" strokeWidth="10" strokeLinecap="round" />
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#34D07A" strokeWidth="10" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} />
      </svg>
      <div className="absolute inset-x-0 bottom-1 text-center">
        <div className="text-3xl font-extrabold text-white">{pct.toFixed(1)}%</div>
        <div className="text-xs text-white/45">of cap earned</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { c: "text-[#D6C51E] border-[#D6C51E]/40 bg-[#D6C51E]/10", icon: Clock, t: "Pending" },
    paid: { c: "text-[#34D07A] border-[#0AA84F]/40 bg-[#0AA84F]/10", icon: CheckCircle2, t: "Paid" },
    rejected: { c: "text-red-400 border-red-500/40 bg-red-500/10", icon: X, t: "Rejected" },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${m.c}`}>
      <Icon size={11} /> {m.t}
    </span>
  );
}

export default function MiningPage() {
  const { address } = useWallet();
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [modal, setModal] = useState(false);
  const [poolInfo, setPoolInfo] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [compoundBusy, setCompoundBusy] = useState(false);
  const [recompoundModal, setRecompoundModal] = useState(false);
  const [cAmount, setCAmount] = useState("");
  const sourcesRef = useRef(null);

  // On open, auto-scroll past the wallet/withdraw header and stop at Earning Sources.
  useEffect(() => {
    const t = setTimeout(() => {
      sourcesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, []);

  const load = useCallback(async () => {
    if (!address) return;
    try { setWallet(await getWallet(address)); } catch (e) { /* ignore */ }
    try { const h = await getWithdrawHistory(address); setHistory(h.rows || []); } catch (e) { /* ignore */ }
  }, [address]);

  useEffect(() => { load(); getSettings().then(setSettings).catch(() => {}); }, [load]);

  const bal = wallet?.balance || {};
  const available = Number(bal.available || 0);
  const cap = Number(wallet?.mining_cap_usd || 0);
  const deposited = Number(wallet?.total_deposited || 0);
  const earned = Number(bal.earned || 0);
  const capPct = cap > 0 ? Math.min(100, (earned / cap) * 100) : 0;
  const minW = settings?.min_withdraw_usd || 5;
  const feePct = settings?.withdraw_fee_pct || 10;
  const amt = Number(amount || 0);
  const fee = amt > 0 ? (amt * feePct) / 100 : 0;
  const net = amt - fee;

  const doWithdraw = async () => {
    if (!amt || amt < minW) return toast.error(`Minimum withdrawal is $${minW}`);
    if (amt > available + 1e-9) return toast.error(`Insufficient balance. Available $${usd2(available)}`);
    setBusy(true);
    try {
      await requestWithdraw({ address, amount: amt });
      notifySuccess("Withdrawal Requested", `$${usd2(net)} (after ${feePct}% fee) will be sent to your wallet`);
      setModal(false);
      setAmount("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Withdrawal failed");
    } finally { setBusy(false); }
  };

  const doRecompound = async () => {
    const cAmt = Number(cAmount);
    if (!cAmt || cAmt < 1) return toast.error("Minimum recompound is $1");
    if (cAmt > available + 1e-9) return toast.error(`Insufficient balance. Available $${usd2(available)}`);
    setCompoundBusy(true);
    try {
      await compound({ address, amount: cAmt });
      notifySuccess("Recompounded", `$${usd2(cAmt)} added to your stake — mining cap increased`);
      setRecompoundModal(false);
      setCAmount("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Recompound failed");
    } finally { setCompoundBusy(false); }
  };

  const dailyRoi = (bal.cap || 0) * 0.005;
  const sources = [
    { label: "Self ROI (1%/day)", value: dailyRoi, suffix: " / day" },
    { label: "Level Income", value: bal.level },
    { label: "Daily Pool", value: bal.daily },
    { label: "Weekly Pool", value: bal.weekly },
    { label: "Monthly Pool", value: bal.monthly },
    { label: "Top Depositor Pool", value: bal.top },
  ];

  return (
    <div data-testid="mining-page" className="flex flex-col gap-4 px-4 pt-4 pb-4">
      <SectionLabel center>My Wallet &amp; Withdrawals</SectionLabel>

      {/* Recompounding Option — exact duplicate of the withdrawal card, placed just above it */}
      <div data-testid="recompound-card" className="card-glow p-5">
        <div className="mb-3 flex items-center gap-2">
          <RefreshCw size={16} className="text-[#34D07A]" />
          <span className="text-base font-bold grad-title">Recompounding Option</span>
        </div>
        <div className="rounded-xl border border-[#3C6B33]/50 bg-black/30 p-4">
          <div className="text-xs uppercase tracking-wide text-[#34D07A]">Recompound (USDT)</div>
          <div data-testid="recompound-available" className="mt-1 text-4xl font-extrabold text-[#34D07A]">
            ${usd2(available)} <span className="text-sm text-white/40">USDT</span>
          </div>
          <div className="mt-1 text-xs text-white/45">
            Total earned ${usd2(earned)} · Withdrawn ${usd2(bal.withdrawn)}
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#D6C51E]/30 bg-[#D6C51E]/8 p-3 text-xs text-white/70">
          <AlertCircle size={14} className="mt-0.5 shrink-0 text-[#D6C51E]" />
          <span className="flex-1">Minimum recompound $1. 0% fee — added straight to your own stake to raise your mining cap. No referral, level or pool income is generated.</span>
        </div>
        <button
          data-testid="recompound-btn"
          disabled={compoundBusy || available < 1}
          onClick={() => { setCAmount(String(available.toFixed(2))); setRecompoundModal(true); }}
          className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0AA84F] via-[#65B82E] to-[#D6C51E] font-bold text-black active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-none disabled:bg-[#3C6B33]/30 disabled:text-white/50"
        >
          <RefreshCw size={16} /> {compoundBusy ? "Recompounding…" : (available >= 1 ? "Recompound" : "Min $1 to recompound")}
        </button>
      </div>

      {/* Available balance */}
      <div data-testid="balance-card" className="card-glow p-5">
        <div className="mb-3 flex items-center gap-2">
          <Wallet size={16} className="text-[#34D07A]" />
          <span className="text-base font-bold grad-title">Available Balance</span>
        </div>
        <div className="rounded-xl border border-[#3C6B33]/50 bg-black/30 p-4">
          <div className="text-xs uppercase tracking-wide text-[#34D07A]">Withdrawable (USDT)</div>
          <div data-testid="available-balance" className="mt-1 text-4xl font-extrabold text-[#34D07A]">
            ${usd2(available)} <span className="text-sm text-white/40">USDT</span>
          </div>
          <div className="mt-1 text-xs text-white/45">
            Total earned ${usd2(earned)} · Withdrawn ${usd2(bal.withdrawn)}
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#D6C51E]/30 bg-[#D6C51E]/8 p-3 text-xs text-white/70">
          <AlertCircle size={14} className="mt-0.5 shrink-0 text-[#D6C51E]" />
          <span className="flex-1">Minimum withdrawal ${minW}. A {feePct}% fee funds the Top Depositor Reward Pool 🏆 — the net amount is sent to your wallet.</span>
          <button data-testid="pool-info-btn" onClick={() => setPoolInfo(true)} aria-label="Pool info" className="info-pulse flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#D6C51E]/50 text-[#D6C51E] active:scale-90">
            <Info size={12} />
          </button>
        </div>
        <button
          data-testid="withdraw-open-btn"
          disabled={available < minW}
          onClick={() => setModal(true)}
          className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0AA84F] via-[#65B82E] to-[#D6C51E] font-bold text-black active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-none disabled:bg-[#3C6B33]/30 disabled:text-white/50"
        >
          <ArrowDownToLine size={16} /> {available >= minW ? "Request Withdrawal" : `Min $${minW} to withdraw`}
        </button>
      </div>

      {/* Earning sources */}
      <div ref={sourcesRef} data-testid="sources-card" className="card-glow scroll-mt-4 p-5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-base font-bold grad-title">Earning Sources</span>
          <button
            data-testid="income-history-btn"
            onClick={() => setHistoryOpen(true)}
            className="hist-breath flex items-center gap-1.5 rounded-xl bg-[#0AA84F]/8 px-4 py-1.5 text-sm font-bold text-[#34D07A] active:scale-95"
          >
            <History size={14} /> History
          </button>
        </div>
        <div className="divide-y divide-white/5">
          {sources.map((s) => (
            <div key={s.label} className="flex items-center justify-between py-2 text-sm">
              <span className="text-white/60">{s.label}</span>
              <span className="font-semibold text-[#34D07A]">
                ${usd2(s.value)}{s.suffix ? <span className="text-xs font-normal text-white/40">{s.suffix}</span> : null}
              </span>
            </div>
          ))}
        </div>
        <div data-testid="earning-total-row" className="total-breath mt-2 flex items-center justify-between rounded-lg px-3 py-2 text-sm">
          <span className="text-white/70">Total Earned</span>
          <span data-testid="earning-total-value" className="font-semibold text-[#34D07A]">${usd2(earned)}</span>
        </div>
      </div>

      {/* Mining cap utilization */}
      <div data-testid="cap-utilization-card" className="card-glow p-5">
        <div className="text-base font-bold grad-title">Mining Cap Utilization</div>
        <div className="mb-2 text-xs text-white/50">Deposited ${usd2(deposited)} · Cap 2x = ${usd2(cap)}</div>
        <Gauge pct={capPct} />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-[#3C6B33]/50 p-3 text-center">
            <div className="text-xs text-white/50">Earned</div>
            <div data-testid="earned-total" className="text-lg font-bold text-[#34D07A]">${usd2(earned)}</div>
          </div>
          <div className="rounded-xl border border-[#3C6B33]/50 p-3 text-center">
            <div className="text-xs text-white/50">Remaining Cap</div>
            <div data-testid="remaining-cap" className="text-lg font-bold text-[#D6C51E]">${usd2(Math.max(0, cap - earned))}</div>
          </div>
        </div>
      </div>

      {/* Withdrawal history */}
      <div data-testid="withdraw-history" className="card-glow p-5">
        <div className="mb-1 flex items-center gap-2">
          <History size={16} className="text-[#34D07A]" />
          <span className="text-base font-bold grad-title">Withdrawal History</span>
        </div>
        <p className="mb-3 text-xs text-[#34D07A]">{history.length} request{history.length === 1 ? "" : "s"}</p>
        <div className="grid grid-cols-4 gap-2 border-t border-white/5 py-2 text-[10px] font-semibold uppercase tracking-wide text-[#D6C51E]">
          <span>Date</span><span className="text-center">Amount</span><span className="text-center">Net</span><span className="text-right">Status</span>
        </div>
        {history.length === 0 ? (
          <div className="border-t border-white/5 py-6 text-center text-xs text-white/45">No withdrawal requests yet.</div>
        ) : (
          history.map((w) => (
            <div key={w.id} data-testid={`withdraw-row-${w.id}`} className="grid grid-cols-4 items-center gap-2 border-t border-white/5 py-2.5 text-xs">
              <span className="text-white/60">{(w.created_at || "").slice(0, 10)}</span>
              <span className="text-center font-semibold text-white">${usd2(w.amount_usd)}</span>
              <span className="text-center text-[#34D07A]">${usd2(w.net_usd)}</span>
              <span className="text-right"><StatusBadge status={w.status} /></span>
            </div>
          ))
        )}
      </div>

      {poolInfo && createPortal(
        <div data-testid="pool-info-modal" className="fixed inset-0 z-[200] flex items-center justify-center p-5 bg-black/75 backdrop-blur-sm" onClick={() => setPoolInfo(false)}>
          <div className="w-full max-w-[380px] rounded-3xl border border-[#D6C51E]/50 bg-[#0A1710] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-bold grad-title"><Trophy size={18} className="text-[#D6C51E]" /> Top Depositor Reward Pool</h3>
              <button data-testid="pool-info-close" onClick={() => setPoolInfo(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60"><X size={16} /></button>
            </div>
            <p className="mb-3 text-xs leading-relaxed text-white/70">
              Every withdrawal's <b className="text-[#D6C51E]">{feePct}% fee</b> goes into this pool. At the end of each 24-hour cycle, the <b className="text-[#34D07A]">top 3 highest depositors</b> win the entire pool — split by rank:
            </p>
            <div className="rounded-xl border border-[#3C6B33]/50 p-1">
              {[["🥇", "Rank 1", "50%"], ["🥈", "Rank 2", "30%"], ["🥉", "Rank 3", "20%"]].map(([m, r, p]) => (
                <div key={r} className="flex items-center justify-between border-b border-white/5 px-3 py-2 text-sm last:border-b-0">
                  <span className="flex items-center gap-2 text-white/70"><span className="text-base">{m}</span> {r}</span>
                  <b className="text-[#34D07A]">{p} of pool</b>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-white/45">
              Example: if the pool holds $20 → Rank 1 gets $10, Rank 2 gets $6, Rank 3 gets $4. Rewards are credited automatically to each winner's withdrawable balance.
            </p>
          </div>
        </div>,
        document.body
      )}

      {modal && createPortal(
        <div data-testid="withdraw-modal" className="fixed inset-0 z-[200] flex items-center justify-center p-5 bg-black/75 backdrop-blur-sm" onClick={() => setModal(false)}>
          <div className="w-full max-w-[380px] rounded-3xl border border-[#3C6B33]/60 bg-[#0A1710] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-bold grad-title">Request Withdrawal</h3>
              <button data-testid="withdraw-modal-close" onClick={() => setModal(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60"><X size={16} /></button>
            </div>
            <div className="mb-2 text-xs text-white/50">Available: <span className="font-semibold text-[#34D07A]">${usd2(available)}</span></div>
            <div className="mb-3 flex items-center rounded-xl border border-white/10 bg-black/30 px-4">
              <span className="text-sm text-white/40">$</span>
              <input data-testid="withdraw-amount-input" type="number" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder={String(minW)}
                className="w-full bg-transparent py-3 pl-1 text-base font-semibold text-white outline-none placeholder:text-white/30" />
              <span className="text-xs font-semibold text-white/40">USDT</span>
            </div>
            <button data-testid="withdraw-max-btn" onClick={() => setAmount(String(available.toFixed(2)))} className="mb-3 text-xs font-semibold text-[#D6C51E]">Use max ${usd2(available)}</button>
            <div className="mb-3 rounded-xl border border-[#3C6B33]/50 p-3 text-xs">
              <div className="flex justify-between py-0.5"><span className="text-white/60">Fee ({feePct}%)</span><span className="text-red-400">-${usd2(fee)}</span></div>
              <div className="flex justify-between py-0.5 font-bold"><span className="text-white/80">You receive</span><span className="text-[#34D07A]">${usd2(net > 0 ? net : 0)}</span></div>
            </div>
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-[#0AA84F]/40 bg-[#0AA84F]/10 p-3">
              <Wallet size={14} className="mt-0.5 shrink-0 text-[#34D07A]" />
              <div className="text-[11px] leading-relaxed text-white/70">
                Sent only to your registered wallet (your ID):
                <div data-testid="withdraw-dest-address" className="mt-0.5 break-all font-mono text-[11px] text-[#34D07A]">{address}</div>
              </div>
            </div>
            <button data-testid="withdraw-submit-btn" disabled={busy} onClick={doWithdraw}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0AA84F] to-[#D6C51E] font-bold text-black disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Confirm Request"}
            </button>
          </div>
        </div>,
        document.body
      )}
      {recompoundModal && createPortal(
        <div data-testid="recompound-modal" className="fixed inset-0 z-[200] flex items-center justify-center p-5 bg-black/75 backdrop-blur-sm" onClick={() => setRecompoundModal(false)}>
          <div className="w-full max-w-[380px] rounded-3xl border border-[#3C6B33]/60 bg-[#0A1710] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-bold grad-title"><RefreshCw size={18} className="text-[#34D07A]" /> Recompound</h3>
              <button data-testid="recompound-modal-close" onClick={() => setRecompoundModal(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60"><X size={16} /></button>
            </div>
            <div className="mb-2 text-xs text-white/50">Available: <span className="font-semibold text-[#34D07A]">${usd2(available)}</span></div>
            <div className="mb-3 flex items-center rounded-xl border border-white/10 bg-black/30 px-4">
              <span className="text-sm text-white/40">$</span>
              <input data-testid="recompound-amount-input" type="number" inputMode="decimal" value={cAmount}
                onChange={(e) => setCAmount(e.target.value)} placeholder="1"
                className="w-full bg-transparent py-3 pl-1 text-base font-semibold text-white outline-none placeholder:text-white/30" />
              <span className="text-xs font-semibold text-white/40">USDT</span>
            </div>
            <button data-testid="recompound-max-btn" onClick={() => setCAmount(String(available.toFixed(2)))} className="mb-3 text-xs font-semibold text-[#D6C51E]">Use max ${usd2(available)}</button>
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-[#D6C51E]/30 bg-[#D6C51E]/8 p-3 text-xs text-white/70">
              <AlertCircle size={14} className="mt-0.5 shrink-0 text-[#D6C51E]" />
              <span className="flex-1">Minimum $1. 0% fee — added straight to your own stake to raise your mining cap. No referral, level or pool income is generated.</span>
            </div>
            <button data-testid="recompound-submit-btn" disabled={compoundBusy} onClick={doRecompound}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0AA84F] via-[#65B82E] to-[#D6C51E] font-bold text-black disabled:opacity-60">
              {compoundBusy ? <Loader2 size={16} className="animate-spin" /> : <><RefreshCw size={16} /> Confirm Recompound</>}
            </button>
          </div>
        </div>,
        document.body
      )}
      {historyOpen && <IncomeHistory address={address} onClose={() => setHistoryOpen(false)} />}
    </div>
  );
}
